"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Cpu, Users, Award } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const defaultMascot = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
    alt: "ROBOTO mascot"
  };
  useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      } catch {
        // Fallback if localStorage is inaccessible
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      } else {
        setLang(getStoredLang());
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const content = {
    railTag: {
      vi: "SỨ MỆNH CÔNG NGHỆ / TIÊU CHUẨN LAB",
      en: "TECHNOLOGY LAB / CORE MISSION"
    },
    archiveTag: {
      vi: "TIÊU CHUẨN ĐÀO TẠO // 01",
      en: "LAB SPECIFICATION // 01"
    },
    archiveLabel: {
      vi: "BÀN THỰC NGHIỆM KỸ THUẬT",
      en: "HARDWARE ENGINEERING BENCH"
    },
    archiveDesc: {
      vi: "Môi trường học tập tiêu chuẩn lab với phần cứng và vi mạch thực tế nhằm phát triển tư duy sáng chế.",
      en: "Hands-on engineering lab environments equipped with real hardware to cultivate genuine problem-solving skills."
    },
    eyebrow: {
      vi: "SỨ MỆNH GIÁO DỤC",
      en: "OUR MISSION"
    },
    title: {
      vi: "VỀ ROBOTO",
      en: "ABOUT ROBOTO"
    },
    subheadline: {
      vi: "Kiến tạo tư duy công nghệ từ những trải nghiệm thực tế.",
      en: "Building technological thinking through real, hands-on experience."
    },
    heading: {
      vi: "Câu chuyện của chúng tôi",
      en: "Our story"
    },
    subtitle: {
      vi: "Thu hẹp khoảng cách giữa lý thuyết học đường và công nghệ thực tiễn.",
      en: "Bridging the gap between classroom theory and real-world engineering."
    },
    bodyIntro: {
      vi: "ROBOTO không chỉ dừng lại ở việc lắp ráp robot đơn thuần—mà là sự kết hợp toàn diện giữa Robotics + Lập trình (Coding) + Kỹ thuật (Engineering) + STEM. Chúng tôi kiến tạo một môi trường học tập thực tiễn, nơi trẻ trực tiếp chế tạo, viết mã, thử nghiệm, sửa lỗi (debug) và giải quyết các bài toán công nghệ thực tế.",
      en: "ROBOTO goes far beyond simple robot assembly—integrating Robotics + Coding + Engineering + STEM into a comprehensive technology ecosystem. We build an authentic learning environment where children directly build, program, test, debug, and solve real-world technology challenges."
    },
    bodyP1: {
      vi: "ROBOTO ra đời nhằm giải quyết một khoảng trống cốt lõi trong giáo dục công nghệ cho thanh thiếu niên: phần lớn các lớp học lập trình hiện nay chỉ dừng lại ở trò chơi đơn thuần trên màn hình, trong khi trường học truyền thống thường nặng về lý thuyết sách vở. Trẻ em luôn tò mò muốn biết máy móc vận hành thế nào, nhưng hiếm khi có cơ hội trực tiếp chạm tay vào vi điều khiển, mạch điện tử hay khung cơ khí chuẩn công nghiệp.",
      en: "ROBOTO exists to address a critical gap in youth technology education: typical coding programs rely almost exclusively on superficial screen games, while traditional classrooms remain tied to textbook theory. Young minds are eager to understand how machines actually work, yet rarely have the opportunity to handle authentic microcontrollers, electronic circuits, or industrial-grade hardware."
    },
    bodyP2: {
      vi: "Chúng tôi xây dựng ROBOTO như một không gian lab thực thụ, nơi học viên từ 4 đến 16 tuổi được định vị là những nhà sáng chế kỹ thuật tương lai chứ không phải người dùng thụ động. Tại đây, kỷ luật kỹ thuật công nghệ được kết hợp hài hòa cùng niềm hứng khởi khám phá: các em hiểu cách cảm biến đọc tín hiệu vật lý, viết mã lệnh Python để giải quyết các thách thức thực tế, và tự tay vận hành những mẫu robot hoàn chỉnh.",
      en: "We created ROBOTO as a genuine lab environment where students aged 4 to 16 are nurtured as future engineers rather than passive digital consumers. Here, technical discipline meets genuine creative discovery: learners explore how sensors interpret physical environments, write Python code to tackle practical challenges, and assemble fully functional robotic systems with their own hands."
    },
    ctaPrimary: {
      vi: "KHÁM PHÁ CHƯƠNG TRÌNH",
      en: "EXPLORE PROGRAMS"
    },
    ctaSecondary: {
      vi: "KHÁM PHÁ LỘ TRÌNH ROBOTICS",
      en: "EXPLORE THE ROBOTICS PATHWAY"
    }
  };
  const milestones = [{
    icon: Cpu,
    label: {
      vi: "Tỷ lệ 1 giảng viên : 6 học viên",
      en: "1:6 Mentor Ratio"
    },
    description: {
      vi: "Sự kèm cặp sát sao từ các chuyên gia Engineering giàu kinh nghiệm.",
      en: "Direct guidance from engineering specialists."
    }
  }, {
    icon: Users,
    label: {
      vi: "Độ tuổi từ 4 đến 16",
      en: "Ages 4 to 16"
    },
    description: {
      vi: "Lộ trình bài bản từ các bộ lắp ráp cơ khí LEGO đến lập trình Python.",
      en: "Structured pathways from mechanical kits to Python."
    }
  }, {
    icon: Award,
    label: {
      vi: "Chuẩn mực STEM quốc tế",
      en: "Global STEM Standards"
    },
    description: {
      vi: "Giáo trình chuẩn lab quốc tế được thiết kế cho các đấu trường Robotics thực tế.",
      en: "Lab-tested curriculum built for real competition."
    }
  }];
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 16
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: shouldReduceMotion ? 0 : 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="our-story" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24 overflow-hidden border-b border-border">
      {/* Precision Blueprint background grid motif */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,hsl(var(--foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--foreground))_1px,transparent_1px)] [background-size:32px_32px]" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        {/* Spec rail accent line */}
        <div className="mb-8 flex items-center gap-2" aria-hidden="true">
          <div className="h-1.5 w-1.5 bg-secondary" />
          <div className="h-px w-24 bg-secondary" />
          <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
            {content.railTag[lang]}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Column 1: Candid photograph of engineering team in workshop */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true
        }} transition={motionTransition} className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_mentors_students_engineering_workbench.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_mentors_students_engineering_workbench.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_mentors_students_engineering_workbench.png-wvc-sizes" alt="ROBOTO mentors and students collaborating around a modular robotics engineering workbench" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[4/3]" />

              {/* Lab Specification Metadata Badge */}
              <div className="absolute bottom-4 left-4 right-4 bg-primary/95 text-primary-foreground p-4 rounded-xl backdrop-blur-md border border-border/20 shadow-md">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-secondary font-semibold">{content.archiveTag[lang]}</span>
                  <span className="text-primary-foreground/75">{content.archiveLabel[lang]}</span>
                </div>
                <p className="text-sm font-sans mt-1 text-primary-foreground/90 font-medium">
                  {content.archiveDesc[lang]}
                </p>
              </div>
            </div>

            {/* Corner Decorative Mechanical Coordinate */}
            <div className="hidden sm:block absolute -top-3 -right-3 h-8 w-8 border-t-2 border-r-2 border-secondary" aria-hidden="true" />
          </motion.div>

          {/* Column 2: Narrative copy */}
          <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
          once: true
        }} transition={{
          ...motionTransition,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-6 flex flex-col justify-center">
            <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold mb-2">
              {content.eyebrow[lang]}
            </span>

            {/* Page's primary single H1 */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.12] mb-3">
              {content.title[lang]}
            </h1>

            <p className="font-serif text-lg sm:text-xl text-primary font-semibold mb-4 leading-snug">
              {content.subheadline[lang]}
            </p>

            <h2 className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground/90 mb-2">
              {content.heading[lang]}
            </h2>

            <p className="text-base sm:text-lg font-sans text-muted-foreground font-medium mb-6 leading-relaxed">
              {content.subtitle[lang]}
            </p>

            <div className="space-y-4 text-base font-sans text-foreground/85 leading-relaxed mb-8">
              <p className="font-medium text-foreground">
                {content.bodyIntro[lang]}
              </p>
              <p>
                {content.bodyP1[lang]}
              </p>
              <p>
                {content.bodyP2[lang]}
              </p>
            </div>

            {/* Core Academy Pillars */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {milestones.map((item, index) => {
              const Icon = item.icon;
              return <Card key={index} data-index={index} className="p-4 rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-secondary hover:shadow-sm">
                    <div className="w-8 h-8 rounded-lg bg-secondary/15 text-secondary border border-secondary/25 flex items-center justify-center mb-3">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="font-serif text-sm font-semibold text-foreground mb-1">
                      {item.label[lang]}
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.description[lang]}
                    </p>
                  </Card>;
            })}
            </div>

            {/* Call to action links */}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-semibold">
                <Link to="/ProgramsPage">
                  {content.ctaPrimary[lang]}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-semibold">
                <Link to="/RoboticsLearningPathwayPage" className="inline-flex items-center gap-1.5">
                  <span>{content.ctaSecondary[lang]}</span>
                  <ArrowRight className="w-4 h-4 text-secondary" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Static Floating Decorative Mascot */}
      <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
      once: true
    }} transition={{
      ...motionTransition,
      delay: shouldReduceMotion ? 0 : 0.2
    }} className="pointer-events-none select-none absolute bottom-3 right-3 sm:bottom-4 sm:right-6 lg:bottom-6 lg:right-10 z-0 hidden sm:block" aria-hidden="true">
        <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
      </motion.div>
    </section>;
}